import { sendEvent } from "../global/utils/sendEvent"

const membership = () => ({
  membershipContainer: null,
  customer: null,
  isLoggedIn: window.__CUSTOMER_LOGGED_IN__ || false,
  // True only for a real Shopify customer. `isLoggedIn` also covers an
  // email-capture signup, who has no account — so anything that would send
  // someone to /account must check this instead, or they get bounced back to
  // the login page. The header account icon is the only such place.
  hasAccount: window.__CUSTOMER_LOGGED_IN__ || false,
  isMemberVariant: true,
  email: "",
  password: "",
  confirmPassword: "",
  firstName: "",
  lastName: "",
  formForShow: "registration",
  emailEntered: false,
  passwordEntered: false,
  passwordConfirmed: false,
  errorMessage: null,
  showPasswordInputs: false,
  submitErrorMessage: null,
  submitSuccessMessage: null,
  loginForm: null,
  passwordRecoverForm: null,
  showMembershipModal: false,
  showMembershipModalSection: false,
  // Variant B (see sections/membership-modal-v2.liquid). There is one section
  // instance per variant; every modal shares `showMembershipModal` and the same
  // triggers, and which one is visible is decided purely in CSS. No variant
  // logic here. `v2View` is the modal's own view state, not a variant switch.
  membershipContainerV2: null,
  v2View: "auth",
  // Set for 7 days when someone signs up through the email-capture variant.
  isEmailMember: false,
  // The v1-styled email modal (Variant C) runs control's sign up for anyone who
  // already signed up by email, so they can still create a real account.
  // Latched when the modal opens, so a fresh signup keeps its success state
  // until the modal is next opened.
  useControlFlow: false,
  processing: false,
  emailIsValid: false,
  openModalTimeOut: null,
  scrollTrigger: 1000,
  mainContainer: null,
  init() {
    this.membershipContainer = document.querySelector(
      ".membership-modal__container"
    )
    this.membershipContainerV2 = document.querySelector(".membership-modal-v2")
    this.loginForm = document.querySelector("#customer_login")
    this.pageTemplateSuffix = document.getElementById("MainContent").getAttribute("data-template-suffix") || ""

    // An email-capture signup counts as logged in for the life of the cookie —
    // member pricing, cart, the lot. `hasAccount` stays false.
    this.isEmailMember = this.getCookie("membershipEmailSignup") === "true"
    if (this.isEmailMember) {
      this.isLoggedIn = true
    }

    // Someone who already signed up through the email variant is not prompted
    // again until the cookie expires. The password reset and account
    // activation pages are where emailed links land, so the modal would cover
    // the form they came to fill in. Keyed off the template the layout exposes,
    // since a failed submit re-renders the page at a bare `/account/reset`; the
    // path check is a fallback.
    const isAccountEmailLanding =
      ["reset_password", "activate_account"].includes(window._template?.name) ||
      /\/account\/(reset|activate)(\/|$)/.test(window.location.pathname)

    if (
      (this.membershipContainer || this.membershipContainerV2) &&
      this.getCookie("closedMembershipModal") !== "true" &&
      !this.isEmailMember &&
      !isAccountEmailLanding &&
      this.pageTemplateSuffix !== "atelier"
    ) {
      setTimeout(() => this.openMemberShipModal({ auto: true }), 1000)
    }
    this.setMemberVariantLineItemProperties()

    // Commenting out for now as we want to enable member variant for all customers, not just logged in ones. We may bring this back if we decide to only show member variant to logged in customers in the future.
    // document.addEventListener('ig:member-variant', (e) => {
    //   this._applyMemberVariant(e.detail === true);
    // }, { once: true });
  },
  _applyMemberVariant(val) {
    // Email-variant signups hold member pricing for the life of their cookie,
    // even if the experiment turns the member variant off for everyone else.
    if (this.isLoggedIn || this.isEmailMember) {
      this.isMemberVariant = true
      this.setMemberVariantLineItemProperties()
      return
    }
    if (val === true) {
      this.isMemberVariant = true
      this.setMemberVariantLineItemProperties()
    } else {
      this.isMemberVariant = false
      this.clearMemberVariantLineItemProperties()
    }
  },
  async clearMemberVariantLineItemProperties() {
    const cart = await fetch("/cart.js").then((r) => r.json())
    for (const item of cart.items) {
      if (!item.properties || item.properties["_member_variant"] !== "true")
        continue
      const updatedProperties = { ...item.properties }
      delete updatedProperties["_member_variant"]
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: updatedProperties,
        }),
      })
    }
  },
  async setMemberVariantLineItemProperties() {
    const cart = await fetch("/cart.js").then((r) => r.json())
    for (const item of cart.items) {
      if (item.properties && item.properties["_member_variant"] === "true")
        continue
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: { ...item.properties, _member_variant: "true" },
        }),
      })
    }
  },
  handleEmail(event) {
    this.emailIsValid = event.target.checkValidity()

    if (this.email && this.emailIsValid) {
      this.emailEntered = true
      this.invalidEmailError = null
    } else {
      this.invalidEmailError = "Please enter valid email address"
    }
  },
  handlePasswords() {
    if (this.password) {
      this.passwordEntered = true
    }
  },
  handlePasswordConfirmation() {
    this.passwordConfirmed = this.password === this.confirmPassword
  },
  checkPasswordConfirmation() {
    if (!this.passwordConfirmed) {
      this.errorMessage = "Passwords must match"
    } else {
      this.errorMessage = ""
    }
  },
  navigateNextStep() {
    this.showPasswordInputs = true
  },
  resetForm() {
    this.email = ""
    this.password = ""
    this.passwordConfirmed = false
    this.errorMessage = ""
    this.submitErrorMessage = ""
    this.emailIsValid = false
    this.showPasswordInputs = false
    this.confirmPassword = ""
  },
  switchForm(formType) {
    let form
    this.formForShow = formType
    this.resetForm()

    window.setTimeout(() => {
      if (formType === "registration") {
        form = document.getElementById("sign-up-form")
      } else if (formType === "login") {
        form = this.loginForm
      }

      if (form) {
        const emailInput = form.querySelector("input[type='email']")
        if (emailInput) {
          window.setTimeout(() => emailInput.focus(), 0)
        }
      }

      this.passwordRecoverForm = document.querySelector("#password_recover")

      if (this.passwordRecoverForm) {
        const emailInput = this.passwordRecoverForm.querySelector(
          "input[type='email']"
        )
        emailInput.focus()
      }
    }, 0)
  },
  memberSignUp(event) {
    sendEvent("Membership Module - Submit")
    try {
      this.processing = true
      fetch(
        "https://cw-shopify-api.sean-8bb.workers.dev/api/customers/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: this.email,
            first_name: this.firstName,
            password: this.password,
            password_confirmation: this.password,
          }),
        }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            // `hasAccount`, not `isLoggedIn`: an email signup creating a real
            // account is already "logged in" and would close before the
            // success message shows.
            this.showMembershipModal = !this.hasAccount
            this.submitSuccessMessage = this.membershipContainer?.getAttribute(
              "data-success-message"
            )
            setTimeout(() => {
              this.isLoggedIn = true
              this.hasAccount = true
              this.memberLogIn(event)
            }, 1000)
          }

          if (data.data.errors) {
            if (data.data.errors.email[0].includes("has already")) {
              sendEvent("Membership Module - Error", {
                event_detail: "Email has already been taken",
              })
              this.submitErrorMessage =
                "Email has already been taken. Forgot password? " +
                `<button @click='$store.membership.switchForm("forgot_password"); $sendEvent("Membership Module - Toggle Form", { event_detail: "Forgot Password" })' class='underline'>Click here</button>` +
                ""
            } else {
              this.submitErrorMessage = `Email ${data.data.errors.email[0]}`
              sendEvent("Membership Module - Error", {
                event_detail: `Email ${data.data.errors.email[0]}`,
              })
            }
            this.processing = false
          }
        })
    } catch (error) {
      console.log("ERROR", error)
    }
  },
  memberLogIn(event) {
    event.preventDefault()
    sendEvent("Membership Module - Log In")
    this.processing = true
    const formData = new FormData(this.loginForm)

    fetch(this.loginForm.action, {
      method: this.loginForm.method || "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok")
        }
        return response.text()
      })
      .then((data) => {
        const container = document.createElement("div")
        container.innerHTML = data

        if (container.querySelector(".login-form-errors li")) {
          this.submitErrorMessage = container.querySelector(
            ".login-form-errors li"
          ).innerText
          this.isLoggedIn = false
        } else {
          this.isLoggedIn = true
          this.hasAccount = true
          this.submitErrorMessage = false
          this.showMembershipModal = !this.isLoggedIn
        }
        if (this.isLoggedIn) {
          if (
            window.location.pathname.includes("login") ||
            window.location.pathname.includes("register")
          ) {
            window.location.href = "/account"
          }
          if (_template.suffix === "atelier"){
            window.scrollTo({top: 0, behavior: 'smooth'})
          }
        }
        this.processing = false
      })
      .catch((error) => {
        this.processing = false
        console.error("Error:", error)
        sendEvent("Membership Module - Error", { event_detail: error.message })
      })
  },
  forgotPassword(event) {
    event.preventDefault()
    sendEvent("Membership Module - Forgot Password")
    this.processing = true
    const formData = new FormData(this.passwordRecoverForm)

    fetch(this.passwordRecoverForm.action, {
      method: this.passwordRecoverForm.method || "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok")
        }
        return response.text()
      })
      .then((data) => {
        const container = document.createElement("div")
        container.innerHTML = data

        if (container.querySelector(".recover-error__message")) {
          this.submitErrorMessage = container.querySelector(
            ".recover-error__message"
          ).innerText
        }

        if (!this.submitErrorMessage) {
          this.submitSuccessMessage = this.membershipContainer?.getAttribute(
            "data-forgot-password-success-message"
          )
        }

        this.processing = false
      })
  },
  // The social buttons come from the One Click Social Login app block, so there
  // is no markup of ours to bind to. Their clicks are caught on the wrapper, and
  // the provider is read off the button's `loginButton('<provider>')` handler.
  socialLoginClick(event) {
    const button = event.target.closest(".btn-social")
    if (!button) return

    const provider =
      button.getAttribute("onclick")?.match(/loginButton\('([^']+)'/)?.[1] ||
      button.innerText.trim()
    sendEvent("Membership Module - Social Login", {
      event_detail: provider.charAt(0).toUpperCase() + provider.slice(1),
    })
  },
  // `track` is off for the auto-close after a completed signup, so Close only
  // counts people who dismissed the modal themselves.
  closeMemberShipModal({ track = true } = {}) {
    this.showMembershipModal = false
    this.resetForm()
    // Both modals close through here; v2 also has its own view to reset.
    this.v2View = "auth"
    this.submitSuccessMessage = null
    this.setCookie("closedMembershipModal", true, 7)
    if (track) {
      sendEvent("Membership Module - Close")
    }
  },

  /* ---------------------------------------------------------------------
     Variant B
     --------------------------------------------------------------------- */

  // Email-capture variant. Subscribes the address to a Klaviyo list instead of
  // creating a Shopify customer — no password, no account. All the Klaviyo
  // config rides on the submitted form as data attributes, so nothing here has
  // to know which variant is live. The v2 handlers read their messages off the
  // nearest `data-*-success-message` ancestor, so they also serve the v1-styled
  // email modal (Variant C, membership-modal.liquid in email mode).
  emailSignUpV2(event) {
    const form = event.target.closest("form")
    if (!form) return

    const listId = form.dataset.klaviyoList
    const companyId = form.dataset.klaviyoCompany

    if (!listId || !companyId) {
      this.submitErrorMessage =
        "Sign up is unavailable right now. Please try again later."
      console.error("Klaviyo list ID or company ID missing on the signup form")
      return
    }

    sendEvent("Membership Module V2 - Email Signup")
    this.processing = true
    this.submitErrorMessage = null

    // `Shopify Tags` is what the existing Klaviyo segments match on. There is
    // no Shopify customer behind this signup, so the values are written to the
    // profile directly.
    const profileProperties = {
      source: form.dataset.klaviyoSource || "Membership Modal",
    }
    const tags = (form.dataset.klaviyoProfileTags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean)

    if (tags.length) {
      profileProperties["Shopify Tags"] = tags
    }

    fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(
        companyId
      )}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-10-15",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: this.email,
                    properties: profileProperties,
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: "list", id: listId } },
            },
          },
        }),
      }
    )
      .then((response) => {
        // A successful subscribe returns 202 with an empty body.
        if (!response.ok) {
          throw new Error(`Klaviyo responded ${response.status}`)
        }

        this.setEmailMember()
        this.submitSuccessMessage = form
          .closest("[data-success-message]")
          ?.getAttribute("data-success-message")
        this.v2View = "success"
        this.processing = false

        // Leave the confirmation up long enough to read, then close, as the
        // account sign up does once its follow-up log in lands. Skipped if
        // they already closed it themselves.
        setTimeout(() => {
          if (this.showMembershipModal && this.submitSuccessMessage) {
            this.closeMemberShipModal({ track: false })
          }
        }, 2000)
      })
      .catch((error) => {
        this.submitErrorMessage = "Something went wrong. Please try again."
        this.processing = false
        console.error("Error:", error)
      })
  },
  // Signing up through the email variant holds member pricing for 7 days
  // without a Shopify account. Everything that keys off `isLoggedIn` flips;
  // `hasAccount` stays false so the header still offers sign in rather than
  // linking to /account, which would bounce them back to the login page.
  setEmailMember() {
    this.setCookie("membershipEmailSignup", true, 7)
    this.isEmailMember = true
    this.isLoggedIn = true
    this.isMemberVariant = true
  },
  v2SwitchView(view) {
    this.v2View = view
    this.resetForm()
    this.submitSuccessMessage = null

    window.setTimeout(() => {
      // There can be more than one v2 modal on the page (one per variant), so
      // focus the one actually on screen rather than the first in the DOM.
      const visibleModal = Array.from(
        document.querySelectorAll(".membership-modal-v2")
      ).find((modal) => modal.offsetParent !== null)
      const emailInput = visibleModal?.querySelector("input[type='email']")

      if (emailInput) {
        emailInput.focus()
      }
    }, 0)
  },
  // Posts a Shopify customer form (login or recover) and hands back the parsed
  // response body, so the v2 handlers can read whichever error node applies.
  submitAccountForm(form) {
    return fetch(form.action, {
      method: form.method || "POST",
      body: new FormData(form),
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok")
        }
        return response.text()
      })
      .then((data) => {
        const container = document.createElement("div")
        container.innerHTML = data
        return container
      })
  },
  // `auto` marks the follow-up log in that runs after a successful sign up, so
  // that automatic call does not report as a user-initiated log in. (v1's
  // memberSignUp calls memberLogIn directly and does inflate its count.)
  memberLogInV2(event, { auto = false } = {}) {
    const form = event.target.closest("form")
    if (!form) return

    if (!auto) {
      sendEvent("Membership Module - Log In")
    }

    this.processing = true
    this.submitErrorMessage = null

    this.submitAccountForm(form)
      .then((container) => {
        const error = container.querySelector(".login-form-errors li")

        if (error) {
          this.submitErrorMessage = error.innerText
          this.isLoggedIn = false
          sendEvent("Membership Module - Error", {
            event_detail: error.innerText,
          })
        } else {
          this.isLoggedIn = true
          this.hasAccount = true
          this.submitErrorMessage = null
          this.showMembershipModal = false

          if (
            window.location.pathname.includes("login") ||
            window.location.pathname.includes("register")
          ) {
            window.location.href = "/account"
          }
        }
        this.processing = false
      })
      .catch((error) => {
        this.processing = false
        console.error("Error:", error)
        sendEvent("Membership Module - Error", { event_detail: error.message })
      })
  },
  memberSignUpV2(event) {
    const form = event.target.closest("form")
    sendEvent("Membership Module - Submit")

    this.processing = true
    this.submitErrorMessage = null

    fetch("https://cw-shopify-api.sean-8bb.workers.dev/api/customers/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      // Single-step form: there is no confirmation field to compare against.
      body: JSON.stringify({
        email: this.email,
        first_name: this.firstName,
        password: this.password,
        password_confirmation: this.password,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          this.submitSuccessMessage = form
            ?.closest("[data-success-message]")
            ?.getAttribute("data-success-message")
          this.v2View = "success"

          setTimeout(() => {
            this.isLoggedIn = true
            this.hasAccount = true
            if (form) {
              this.memberLogInV2({ target: form }, { auto: true })
            }
          }, 1000)
          return
        }

        const errors = data.data?.errors

        if (errors?.email) {
          if (errors.email[0].includes("has already")) {
            this.submitErrorMessage =
              "Email has already been taken. Sign in above, or use Forgot Password below."
            sendEvent("Membership Module - Error", {
              event_detail: "Email has already been taken",
            })
          } else {
            this.submitErrorMessage = `Email ${errors.email[0]}`
            sendEvent("Membership Module - Error", {
              event_detail: `Email ${errors.email[0]}`,
            })
          }
        } else {
          // Anything else (e.g. a password under Shopify's 5 character
          // minimum) used to fail silently. Show the first error as given.
          const [field, messages] = Object.entries(errors || {})[0] || []
          const message = Array.isArray(messages) ? messages[0] : messages

          this.submitErrorMessage =
            field && message
              ? `${field.charAt(0).toUpperCase()}${field
                  .slice(1)
                  .replace(/_/g, " ")} ${message}`
              : "Something went wrong. Please try again."
          sendEvent("Membership Module - Error", {
            event_detail: this.submitErrorMessage,
          })
        }

        this.processing = false
      })
      .catch((error) => {
        this.processing = false
        console.error("ERROR", error)
        sendEvent("Membership Module - Error", { event_detail: error.message })
      })
  },
  forgotPasswordV2(event) {
    const form = event.target.closest("form")
    if (!form) return

    sendEvent("Membership Module - Forgot Password")
    this.processing = true
    this.submitErrorMessage = null

    this.submitAccountForm(form)
      .then((container) => {
        const error = container.querySelector(".recover-error__message")

        if (error) {
          this.submitErrorMessage = error.innerText
          sendEvent("Membership Module - Error", {
            event_detail: error.innerText,
          })
        } else {
          this.submitSuccessMessage = form
            .closest("[data-forgot-password-success-message]")
            ?.getAttribute("data-forgot-password-success-message")
          this.v2View = "success"
        }

        this.processing = false
      })
      .catch((error) => {
        this.processing = false
        console.error("Error:", error)
        sendEvent("Membership Module - Error", { event_detail: error.message })
      })
  },
  // `auto` marks the timed open on page load, so the event can be split from
  // opens the visitor triggered themselves.
  openMemberShipModal({ auto = false } = {}) {
    this.showMembershipModal = true
    this.useControlFlow = this.isEmailMember
    if (this.openModalTimeOut) {
      clearTimeout(this.openModalTimeOut)
    }

    const form = document.getElementById("sign-up-form")

    if (form) {
      const emailInput = form.querySelector("input[type='email']")

      if (emailInput) {
        window.setTimeout(() => emailInput.focus(), 200)
      }
    }
    sendEvent("Membership Module - Open", {
      event_detail: auto ? "Auto" : "Manual",
    })
  },

  closeMemberShipModalSection() {
    this.showMembershipModalSection = false
    this.resetForm()
  },
  openMemberShipModalSection() {
    this.showMembershipModalSection = true
    if (this.openModalTimeOut) {
      clearTimeout(this.openModalTimeOut)
    }

    const form = document.getElementById("sign-up-form")

    if (form) {
      const emailInput = form.querySelector("input[type='email']")

      if (emailInput) {
        window.setTimeout(() => emailInput.focus(), 200)
      }
    }
  },
  setCookie(name, value, days) {
    var expires = ""
    if (days) {
      var date = new Date()
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
      expires = "; expires=" + date.toUTCString()
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/"
  },
  getCookie(name) {
    var nameEQ = name + "="
    var ca = document.cookie.split(";")
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i]
      while (c.charAt(0) == " ") c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length)
    }
    return null
  },
  eraseCookie(name) {
    document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;"
  },
})

export default membership
